import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import {
  DvCheckboxElement,
  DvDatePickerLegacyElement,
  DvDateTimePickerElement,
  DvDropdownClientSideElement,
  DvDynamicFormElement,
  DvNumberboxElement,
  DvRadioGroupLegacyElement,
  DvSlideToggleElement,
  DvSpaceElement,
  DvTextboxElement,
} from '@devinco/ui/legacy';
import { Entity } from '@shared/constants/entity';
import { TranslateService } from '@ngx-translate/core';
import {
  ReportFrequency,
  WeekdaysFormKeys,
} from '@pages/reports-page/components/report-tabs/report-information-tab/constants';
import { FrequencyDropdownItem } from '@pages/reports-page/components/report-tabs/report-information-tab/interfaces';
import { dvNumberMaskPresets } from '@devinco/ui/dds';
import { activeRecordFilter } from '@shared/constants/active-record-filter';

@Injectable()
export class ReportInformationTabService {
  private api = inject(RestApiService);
  private translate = inject(TranslateService);

  // exclude multiselect dropdowns labelValue keys from request because
  // they're not in the ReportSetting entity
  readonly excludedRequestValues = [
    'DepartmentDescription',
    'Description',
    'UserNameDescription',
    'label',
    'Name',
  ];

  monthDropdownItems = [
    { id: 1, label: this.translate.instant('General.Months.January') },
    { id: 2, label: this.translate.instant('General.Months.February') },
    { id: 3, label: this.translate.instant('General.Months.March') },
    { id: 4, label: this.translate.instant('General.Months.April') },
    { id: 5, label: this.translate.instant('General.Months.May') },
    { id: 6, label: this.translate.instant('General.Months.June') },
    { id: 7, label: this.translate.instant('General.Months.July') },
    { id: 8, label: this.translate.instant('General.Months.August') },
    { id: 9, label: this.translate.instant('General.Months.September') },
    { id: 10, label: this.translate.instant('General.Months.October') },
    { id: 11, label: this.translate.instant('General.Months.November') },
    { id: 12, label: this.translate.instant('General.Months.December') },
  ];

  offsetDropdownItems = [
    { id: 1, label: this.translate.instant('Reports.Offset.First') },
    { id: 2, label: this.translate.instant('Reports.Offset.Second') },
    { id: 3, label: this.translate.instant('Reports.Offset.Third') },
    { id: 4, label: this.translate.instant('Reports.Offset.Fourth') },
  ];

  weekdayDropdownItems = [
    {
      id: Number(WeekdaysFormKeys.Mon),
      label: this.translate.instant('General.DaysOfWeek.Monday'),
    },
    {
      id: Number(WeekdaysFormKeys.Tue),
      label: this.translate.instant('General.DaysOfWeek.Tuesday'),
    },
    {
      id: Number(WeekdaysFormKeys.Wed),
      label: this.translate.instant('General.DaysOfWeek.Wednesday'),
    },
    {
      id: Number(WeekdaysFormKeys.Thu),
      label: this.translate.instant('General.DaysOfWeek.Thursday'),
    },
    {
      id: Number(WeekdaysFormKeys.Fri),
      label: this.translate.instant('General.DaysOfWeek.Friday'),
    },
    {
      id: Number(WeekdaysFormKeys.Sat),
      label: this.translate.instant('General.DaysOfWeek.Saturday'),
    },
    {
      id: Number(WeekdaysFormKeys.Sun),
      label: this.translate.instant('General.DaysOfWeek.Sunday'),
    },
  ];

  getMainElements = (): DvDynamicFormElement[] => [
    new DvTextboxElement({
      colspan: 12,
      key: 'ReportDescription',
      label: 'Report.ReportDescription',
      validators: [Validators.required],
    }),
    new DvCheckboxElement({
      key: 'EmptyResultNoReport',
      colspan: 12,
      checkboxLabel: 'Report.EmptyResultNoReport',
    }),
  ];

  getReceiverElements = (): DvDynamicFormElement[] => [
    new DvDropdownClientSideElement({
      isClearable: false,
      isMultipleSelectionEnabled: true,
      closeOnSelect: false,
      isCheckboxEnabled: true,
      colspan: 6,
      key: 'Roles',
      label: 'Report.Roles',
      labelKeys: ['Name'],
      valueKey: 'RoleID',
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.Role,
          fields: ['RoleID', 'Name', 'TranslationKey'],
        };
        this.api.setTranslatedDropdownData(
          request,
          params,
          'TranslationKey',
          undefined,
          'Name'
        );
      },
    }),
    new DvSpaceElement({
      colspan: 6,
    }),
    new DvDropdownClientSideElement({
      colspan: 6,
      isClearable: true,
      closeOnSelect: false,
      isCheckboxEnabled: true,
      key: 'Departments',
      label: 'Report.Departments',
      isMultipleSelectionEnabled: true,
      valueKey: 'DepartmentID',
      labelKeys: ['DepartmentDescription'],
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.Department,
          fields: ['DepartmentID', 'DepartmentDescription'],
          filterModel: activeRecordFilter,
        };
        this.api.setDropdownData(request, params);
      },
    }),
    new DvDropdownClientSideElement({
      colspan: 6,
      isClearable: true,
      closeOnSelect: false,
      isCheckboxEnabled: true,
      key: 'Users',
      label: 'Report.Users',
      isMultipleSelectionEnabled: true,
      valueKey: 'UserID',
      labelKeys: ['UserNameDescription'],
      open: params => {
        const request: GetRowsRequest = {
          entity: Entity.User,
          fields: ['UserID', 'UserNameDescription'],
        };
        this.api.setDropdownData(request, params);
      },
    }),
  ];

  getRecurrenceElements = (): DvDynamicFormElement[] => [
    new DvSlideToggleElement({
      key: 'RecurrenceActive',
      colspan: 12,
      slideToggleLabel: this.translate.instant('Report.RepeatOn'),
    }),
    new DvDatePickerLegacyElement({
      key: 'RecurrenceStartTime',
      colspan: 6,
      label: 'Report.RecurrenceStartTime',
      validators: [Validators.required],
    }),
    new DvSpaceElement({
      colspan: 6,
    }),
    new DvNumberboxElement({
      colspan: 3,
      key: 'Interval',
      label: 'Report.RepeatEvery',
      fieldMask: dvNumberMaskPresets.FormattedDecimalNumber,
      validators: [Validators.required, Validators.min(1)],
    }),
    new DvDropdownClientSideElement({
      colspan: 3,
      key: 'Frequency',
      label: 'Report.Frequency',
      labelKeys: ['label'],
      valueKey: 'value',
      isClearable: false,
      items: [
        {
          value: ReportFrequency.Daily,
          label: this.translate.instant('Reports.Frequency.Day'),
        },
        {
          value: ReportFrequency.Weekly,
          label: this.translate.instant('Reports.Frequency.Week'),
        },
        {
          value: ReportFrequency.Monthly,
          label: this.translate.instant('Reports.Frequency.Month'),
        },
        {
          value: ReportFrequency.Yearly,
          label: this.translate.instant('Reports.Frequency.Year'),
        },
      ] as unknown as FrequencyDropdownItem[],
      validators: [Validators.required],
    }),
    new DvSpaceElement({
      colspan: 6,
    }),
  ];

  getWeekFrequencyElements = (): DvDynamicFormElement[] => [
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Mon,
      colspan: 1,
      label: 'General.DaysOfWeekShort.Monday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Tue,
      colspan: 1,
      label: 'General.DaysOfWeekShort.Tuesday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Wed,
      colspan: 1,
      label: 'General.DaysOfWeekShort.Wednesday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Thu,
      colspan: 1,
      label: 'General.DaysOfWeekShort.Thursday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Fri,
      colspan: 1,
      label: 'General.DaysOfWeekShort.Friday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Sat,
      colspan: 1,
      label: 'General.DaysOfWeekShort.Saturday',
      extra: { ignoreInRequest: true },
    }),
    new DvCheckboxElement({
      key: WeekdaysFormKeys.Sun,
      colspan: 1,
      label: 'General.DaysOfWeekShort.Sunday',
      extra: { ignoreInRequest: true },
    }),
  ];

  getMonthFrequencyElements = (): DvDynamicFormElement[] => [
    new DvRadioGroupLegacyElement({
      colspan: 3,
      key: 'onDay',
      direction: 'vertical',
      labelKey: 'label',
      valueKey: 'value',
      items: [{ value: true, label: this.translate.instant('Reports.OnDay') }],
      gridSize: 1,
      extra: { ignoreInRequest: true },
      hintLeft: ' ',
    }),
    new DvNumberboxElement({
      colspan: 3,
      key: 'ByMonthDay',
      fieldMask: dvNumberMaskPresets.FormattedDecimalNumber,
      validators: [Validators.min(1), Validators.max(31), Validators.required],
      hintLeft: ' ',
    }),
    new DvSpaceElement({
      colspan: 6,
    }),
    new DvRadioGroupLegacyElement({
      colspan: 3,
      key: 'onThe',
      direction: 'vertical',
      labelKey: 'label',
      valueKey: 'value',
      items: [{ value: true, label: this.translate.instant('Reports.OnThe') }],
      gridSize: 1,
      extra: { ignoreInRequest: true },
    }),
    new DvDropdownClientSideElement({
      colspan: 3,
      key: 'Offset',
      labelKeys: ['label'],
      valueKey: 'id',
      isClearable: false,
      extra: { ignoreInRequest: true },
      items: this.offsetDropdownItems,
      validators: [Validators.required],
    }),
    new DvDropdownClientSideElement({
      colspan: 3,
      key: 'ByDay',
      labelKeys: ['label'],
      valueKey: 'id',
      isClearable: false,
      items: this.weekdayDropdownItems,
      validators: [Validators.required],
    }),
    new DvSpaceElement({
      colspan: 3,
    }),
  ];

  getYearFrequencyElements = (): DvDynamicFormElement[] => [
    new DvRadioGroupLegacyElement({
      colspan: 3,
      key: 'on',
      direction: 'vertical',
      labelKey: 'label',
      valueKey: 'value',
      items: [{ value: true, label: this.translate.instant('Reports.On') }],
      gridSize: 1,
      extra: { ignoreInRequest: true },
      hintLeft: ' ',
    }),
    new DvDropdownClientSideElement({
      colspan: 3,
      key: 'ByMonthWithDay',
      labelKeys: ['label'],
      valueKey: 'id',
      extra: { ignoreInRequest: true },
      items: this.monthDropdownItems,
      isClearable: false,
      validators: [Validators.required],
      hintLeft: ' ',
    }),
    new DvNumberboxElement({
      colspan: 3,
      key: 'ByMonthDay',
      fieldMask: dvNumberMaskPresets.FormattedDecimalNumber,
      validators: [Validators.min(1), Validators.max(31), Validators.required],
      hintLeft: '  ',
    }),

    new DvSpaceElement({
      colspan: 3,
    }),
    new DvRadioGroupLegacyElement({
      colspan: 3,
      key: 'onThe',
      direction: 'vertical',
      labelKey: 'label',
      valueKey: 'value',
      items: [{ value: true, label: this.translate.instant('Reports.OnThe') }],
      gridSize: 1,
      extra: { ignoreInRequest: true },
    }),
    new DvDropdownClientSideElement({
      colspan: 3,
      key: 'Offset',
      labelKeys: ['label'],
      valueKey: 'id',
      extra: { ignoreInRequest: true },
      items: this.offsetDropdownItems,
      isClearable: false,
      validators: [Validators.required],
    }),
    new DvDropdownClientSideElement({
      colspan: 3,
      key: 'ByDay',
      labelKeys: ['label'],
      valueKey: 'id',
      isClearable: false,
      items: this.weekdayDropdownItems,
    }),
    new DvDropdownClientSideElement({
      colspan: 3,
      key: 'ByMonthWithOffset',
      labelKeys: ['label'],
      valueKey: 'id',
      extra: { ignoreInRequest: true },
      items: this.monthDropdownItems,
      isClearable: false,
      validators: [Validators.required],
    }),
  ];

  getLastSentElements = (): DvDynamicFormElement[] => [
    new DvDateTimePickerElement({
      colspan: 12,
      key: 'LastSentTime',
      label: 'Report.LastSentTime',
      disabled: true,
    }),
    new DvCheckboxElement({
      key: 'SendNow',
      disabled: true,
      hidden: true,
    }),
  ];
}
